import React, { useEffect, useRef } from 'react';
import { GlobalApiBridge, GlobalConfig } from '@zds3/zds-components';
import { useLighthouseSettings } from '../hooks/useLighthouseSettings';
import { useNavigationVariant } from '../hooks/useNavigationVariant';
import { NavigationOverrides } from '../NavigationContext';

/**
 * Billing wires the ZDS `GlobalApiBridge` to the billing `userPreference` API
 * (settings persisted under `userSettings.lighthouse`).
 */
const BillingNavigationApiIntegration = () => {
  const {
    userPreferences,
    isUserPreferencesLoading,
    hasFreshUserPreferences,
    getSetting,
    updateSetting,
    removeSetting,
  } = useLighthouseSettings();

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (
      isUserPreferencesLoading ||
      !hasFreshUserPreferences ||
      !userPreferences ||
      hasInitialized.current
    ) {
      return;
    }

    hasInitialized.current = true;

    GlobalApiBridge.getInstance().registerUserPreferencesHandlers(
      updateSetting,
      getSetting,
      removeSetting,
    );
    GlobalConfig.configurePersistence();
  }, [
    isUserPreferencesLoading,
    hasFreshUserPreferences,
    userPreferences,
    getSetting,
    updateSetting,
    removeSetting,
  ]);

  return null;
};

/**
 * Revenue does not use the billing `userPreference` API, so there is nothing to
 * wire up here yet. Kept as a dedicated placeholder so revenue-specific
 * persistence can be added later without touching the billing path (and without
 * the billing preference hooks running on revenue routes).
 */
const RevenueNavigationApiIntegration = () => {
  // TODO: wire up revenue-specific settings persistence when available.
  return null;
};

export const NavigationApiIntegration = ({
  variant: variantOverride,
}: NavigationOverrides = {}) => {
  const variant = useNavigationVariant({ variant: variantOverride });

  if (variant === 'revenue') {
    return <RevenueNavigationApiIntegration />;
  }

  return <BillingNavigationApiIntegration />;
};
